import React from "react";
import { Link } from "react-router-dom";

// App-level crash containment. Before this existed, any render throw in a page
// component bubbled to the root and blanked the WHOLE app (navbar + footer + an
// empty main) — the amplifier behind the /previews outage. This catches the
// throw and renders a contained, navigable card instead, so the chrome survives
// and the user can move on.
//
// `resetKey` (wired to location.pathname in App) clears the error when the route
// changes, so navigating away from a crashed page recovers without a full reload.
//
// `data-testid="error-boundary"` is a stable marker the site health check asserts
// against — a caught error does NOT fire window.onerror/pageerror, so without this
// marker the boundary would hide real crashes from the monitor.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the console for devtools + the health-check console listener.
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught a render error:", error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div
          className="container-narrow"
          data-testid="error-boundary"
          style={{ padding: "4rem 1rem", textAlign: "center" }}
        >
          <div style={{ fontFamily: "var(--font-hand)", fontSize: "1.4rem", color: "var(--terracotta)", marginBottom: 8 }}>
            Something went wrong
          </div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: "2.2rem", color: "var(--ink)", marginBottom: 14 }}>
            This page hit an error
          </h1>
          <p style={{ color: "var(--pencil)", fontSize: "1rem", maxWidth: 460, margin: "0 auto 28px", lineHeight: 1.7 }}>
            Sorry — something broke while rendering this page. The rest of the
            site is fine; try again, or head back and pick up elsewhere.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Reload the page
            </button>
            <Link to="/" className="btn btn-secondary">Home</Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
